import { IndexedDbKV } from './IndexedDbKV';
import { getTokenFromCookies } from './cookies';

export const SOCIAL_INTEGRATION_STATE_KEY = 'socialIntegrationState';
export const SOCIAL_INTEGRATION_PROVIDER_KEY = 'socialIntegrationProvider';
export const SOCIAL_INTEGRATION_TAB_ID_KEY = 'socialIntegrationTabId';
// Long enough to log in and authorize, short enough not to resurface an abandoned form.
export const SOCIAL_INTEGRATION_TTL_MS = 30 * 60 * 1000;
export type SocialIntegrationState = { values: unknown };
type StoredIntegration<T> = T & {
  fileStorageKey?: string | null;
  savedAt?: number;
  userId?: string | null;
  tabId?: string;
};
export type PendingSocialIntegration = {
  provider: string;
  // False when the provider app sent the user back in a different tab.
  startedInThisTab: boolean;
};

const readFileBytes = (file: File): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

const copyFilesToMemory = async (
  files: Record<string, File>
): Promise<Record<string, File>> =>
  Object.fromEntries(
    await Promise.all(
      Object.entries(files).map(async ([name, file]) => [
        name,
        new File([await readFileBytes(file)], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        }),
      ])
    )
  );

const createRandomId = () =>
  Array.from(crypto.getRandomValues(new Uint32Array(4))).join('-');

// IndexedDB is shared by tabs, so files belong to an attempt rather than a provider.
const createFileStorageKey = () => `socialIntegration:${createRandomId()}`;

// sessionStorage is per tab, so it identifies the tab that started an attempt.
const getTabId = () => {
  let tabId = sessionStorage.getItem(SOCIAL_INTEGRATION_TAB_ID_KEY);
  if (!tabId) {
    tabId = createRandomId();
    sessionStorage.setItem(SOCIAL_INTEGRATION_TAB_ID_KEY, tabId);
  }
  return tabId;
};

const getCurrentUserId = () => getTokenFromCookies()?.id ?? null;

export const createSocialIntegrationStorage = (
  stateKey: string,
  providerKey: string
) => {
  let filesDb: IndexedDbKV | undefined;
  const getFilesDb = () => {
    if (!filesDb) filesDb = new IndexedDbKV('kv', 'socialIntegrationFile');
    return filesDb;
  };

  const save = async <T extends SocialIntegrationState>(
    state: T,
    provider: string,
    files?: Record<string, File>
  ) => {
    const fileStorageKey =
      files && Object.keys(files).length ? createFileStorageKey() : null;
    // localStorage, not sessionStorage: on mobile the provider app often sends the
    // user back to a new tab, which has to be able to pick the attempt up.
    const serializedState = JSON.stringify({
      ...state,
      fileStorageKey,
      savedAt: Date.now(),
      userId: getCurrentUserId(),
      tabId: getTabId(),
    });
    try {
      if (fileStorageKey) await getFilesDb().setMany([[fileStorageKey, files]]);
      localStorage.setItem(stateKey, serializedState);
      // Publishing the provider commits the attempt after all its data is saved.
      localStorage.setItem(providerKey, provider);
    } catch (error) {
      if (localStorage.getItem(stateKey) === serializedState) {
        localStorage.removeItem(stateKey);
        localStorage.removeItem(providerKey);
      }
      if (fileStorageKey)
        await getFilesDb()
          .delete(fileStorageKey)
          .catch(() => undefined);
      throw error;
    }
  };

  const discard = (serializedState: string | null) => {
    if (localStorage.getItem(stateKey) === serializedState) {
      localStorage.removeItem(providerKey);
      localStorage.removeItem(stateKey);
    }
  };

  // Returns the saved attempt if this user may still continue it, dropping it otherwise.
  const getPending = (): PendingSocialIntegration | null => {
    const provider = localStorage.getItem(providerKey);
    if (!provider) return null;
    const serializedState = localStorage.getItem(stateKey);
    let parsedState: StoredIntegration<SocialIntegrationState> | null = null;
    try {
      parsedState = serializedState ? JSON.parse(serializedState) : null;
    } catch {
      parsedState = null;
    }
    const isExpired =
      !parsedState?.savedAt ||
      Date.now() - parsedState.savedAt > SOCIAL_INTEGRATION_TTL_MS;
    const isOtherUser = parsedState?.userId !== getCurrentUserId();
    if (!parsedState || isExpired || isOtherUser) {
      discard(serializedState);
      if (parsedState?.fileStorageKey)
        getFilesDb()
          .delete(parsedState.fileStorageKey)
          .catch(() => undefined);
      return null;
    }
    return {
      provider,
      startedInThisTab:
        parsedState.tabId ===
        sessionStorage.getItem(SOCIAL_INTEGRATION_TAB_ID_KEY),
    };
  };

  const read = async <T extends SocialIntegrationState>() => {
    const serializedState = localStorage.getItem(stateKey);
    if (!serializedState)
      throw new Error('Saved social integration state is missing');
    const parsedState = JSON.parse(serializedState) as StoredIntegration<T>;
    if (!parsedState?.values || typeof parsedState.values !== 'object') {
      throw new Error('Saved social integration state is invalid');
    }
    const { fileStorageKey } = parsedState;
    // Only the caller's own state is handed back, not the attempt bookkeeping.
    const state = { ...parsedState };
    delete state.fileStorageKey;
    delete state.savedAt;
    delete state.userId;
    delete state.tabId;
    const values = parsedState.values as Record<string, unknown>;
    let files: Record<string, File> | undefined;
    let fileKeys: IDBValidKey[] = [];
    if (fileStorageKey) {
      files = await getFilesDb().get<Record<string, File>>(fileStorageKey);
      if (!files) throw new Error('Saved social integration files are missing');
      fileKeys = [fileStorageKey];
    } else if (
      fileStorageKey === undefined &&
      stateKey === SOCIAL_INTEGRATION_STATE_KEY &&
      typeof values.avatar !== 'string'
    ) {
      // Compatibility for avatars saved by the previous release. New file-free attempts use null.
      const legacyFiles = (await getFilesDb().getMany()).filter(
        ([key, value]) => key === 'avatar' && value instanceof File
      );
      if (legacyFiles.length) files = Object.fromEntries(legacyFiles);
      fileKeys = legacyFiles.map(([key]) => key);
    }
    if (files) files = await copyFilesToMemory(files);

    const clear = async () => {
      await Promise.all(fileKeys.map((key) => getFilesDb().delete(key)));
      // An older callback must not remove a newly saved attempt.
      discard(serializedState);
    };
    return { state: state as T, files, clear };
  };
  return { save, read, getPending };
};
